import { useMutation } from "@tanstack/react-query";
import { Download, Eye, FileArchive, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import EditRecordModal from "@/features/records/EditRecordModal";
import { ApiError } from "@/lib/api/client";
import { downloadJobPdfZip, type CsvType, type InvoiceFileOut } from "@/lib/api/jobs";
import { cn } from "@/lib/cn";

import CsvPreviewModal from "./CsvPreviewModal";
import DeliveryQaPanel from "./DeliveryQaPanel";
import { useDownloadCsv } from "./useDownloadCsv";
import { useJobQa } from "./useQa";

interface Props {
  jobId: string;
  successCount: number;
  rejectedCount: number;
  /** 미리보기 행 클릭 → 케이스 편집 모달을 여는 데 필요한 파일 목록. */
  files: InvoiceFileOut[];
}

interface Variant {
  type: CsvType;
  /** ``jobs.detail.csv.variants.{key}.label`` 의 sub-key. */
  i18nKey: "delivery_ok" | "delivery_rejected" | "review_ok" | "review_rejected";
  group: "delivery" | "review";
  showWhen: "ok" | "rejected";
}

/**
 * CSV 다운로드 4종 정의.
 *
 * 라벨/힌트는 i18n 키만 가진다 — 화면에서 ``t(`...${i18nKey}.label`)``,
 * ``t(`...${i18nKey}.hint`)`` 로 렌더 시점에 해석. 옵션 상수 자체가
 * 언어에 의존하지 않도록 분리.
 */
function _triggerBrowserDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const VARIANTS: Variant[] = [
  { type: "delivery", i18nKey: "delivery_ok", group: "delivery", showWhen: "ok" },
  {
    type: "rejected_delivery",
    i18nKey: "delivery_rejected",
    group: "delivery",
    showWhen: "rejected",
  },
  { type: "review", i18nKey: "review_ok", group: "review", showWhen: "ok" },
  { type: "rejected_review", i18nKey: "review_rejected", group: "review", showWhen: "rejected" },
];

export default function CsvDownloadButtons({ jobId, successCount, rejectedCount, files }: Props) {
  const { t } = useTranslation();
  const download = useDownloadCsv();
  const qa = useJobQa(jobId);
  const pdfZip = useMutation({
    mutationFn: async () => {
      const { blob, filename } = await downloadJobPdfZip(jobId);
      _triggerBrowserDownload(blob, filename);
    },
  });
  const hasFiles = files.length > 0;
  const [showPreview, setShowPreview] = useState(false);
  // 미리보기에서 클릭해 편집 중인 케이스 파일 id.
  const [editingFileId, setEditingFileId] = useState<string | null>(null);
  const editingFile = useMemo(
    () => files.find((f) => f.id === editingFileId) ?? null,
    [files, editingFileId],
  );

  function isEnabled(v: Variant): boolean {
    // 다운로드 차단은 풀었다(FB 2026-09-15). QA 는 '경고'만 하고 다운로드 자체는
    // 막지 않는다 — 정책 위반(block)/경고(warn) 상태값·표시는 그대로 유지하되,
    // 납품용(ok) 다운로드는 확인창을 거치면 진행된다.
    if (v.showWhen === "ok") return successCount > 0;
    return rejectedCount > 0;
  }

  const anyData = successCount > 0 || rejectedCount > 0;

  // QA 이슈(정책 위반/정합성/형식 등)가 있으면 확인 후에만 납품용 다운로드 진행.
  function handleDownload(v: Variant): void {
    const hasIssues = !!qa.data && (qa.data.blocking || qa.data.has_warnings);
    if (v.showWhen === "ok" && hasIssues && !window.confirm(t("qa.downloadConfirm"))) {
      return;
    }
    download.mutate({ jobId, type: v.type });
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-800">{t("jobs.detail.csv.title")}</h2>
          <p className="mt-0.5 text-xs text-slate-500">{t("jobs.detail.csv.description")}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => pdfZip.mutate()}
            disabled={!hasFiles || pdfZip.isPending}
            title={t("jobs.detail.pdfZip.hint")}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition",
              hasFiles
                ? "border-slate-300 text-slate-700 hover:bg-slate-50"
                : "cursor-not-allowed border-slate-100 text-slate-300",
              pdfZip.isPending && "cursor-wait",
            )}
          >
            {pdfZip.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <FileArchive className="h-3.5 w-3.5" />
            )}
            {t("jobs.detail.pdfZip.button")}
          </button>
          <button
            type="button"
            onClick={() => setShowPreview(true)}
            disabled={!anyData}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition",
              anyData
                ? "border-slate-300 text-slate-700 hover:bg-slate-50"
                : "cursor-not-allowed border-slate-100 text-slate-300",
            )}
          >
            <Eye className="h-3.5 w-3.5" />
            {t("jobs.detail.csv.previewButton")}
          </button>
        </div>
      </div>
      {pdfZip.isError && (
        <p
          role="alert"
          className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
        >
          {t("jobs.detail.pdfZip.error")}:{" "}
          {pdfZip.error instanceof ApiError ? pdfZip.error.detail : pdfZip.error?.message}
        </p>
      )}

      {anyData && (
        <div className="mt-3">
          <DeliveryQaPanel
            report={qa.data}
            isLoading={qa.isLoading}
            isError={qa.isError}
            onEditItem={(it) => setEditingFileId(it.file_id)}
          />
        </div>
      )}

      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {VARIANTS.map((v) => {
          const enabled = isEnabled(v);
          const isPending = download.isPending && download.variables?.type === v.type;
          return (
            <button
              key={v.type}
              type="button"
              onClick={() => handleDownload(v)}
              disabled={!enabled || download.isPending}
              className={cn(
                "flex items-center justify-between rounded-md border px-3 py-2.5 text-left text-xs transition",
                enabled
                  ? "border-slate-200 hover:border-blue-300 hover:bg-blue-50/50"
                  : "border-slate-100 bg-slate-50 text-slate-400",
                download.isPending && "cursor-wait",
              )}
            >
              <div className="min-w-0">
                <p className={cn("font-medium", enabled ? "text-slate-800" : "text-slate-400")}>
                  {t(`jobs.detail.csv.variants.${v.i18nKey}.label`)}
                </p>
                <p className="mt-0.5 text-[10px] text-slate-400">
                  {t(`jobs.detail.csv.variants.${v.i18nKey}.hint`)}
                </p>
              </div>
              {isPending ? (
                <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
              ) : (
                <Download className={cn("h-4 w-4", enabled ? "text-blue-500" : "text-slate-300")} />
              )}
            </button>
          );
        })}
      </div>

      {download.isError && (
        <p
          role="alert"
          className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
        >
          {t("jobs.detail.csv.errorPrefix")}: {download.error.message}
        </p>
      )}

      {showPreview && (
        <CsvPreviewModal
          jobId={jobId}
          successCount={successCount}
          rejectedCount={rejectedCount}
          onClose={() => setShowPreview(false)}
          onEditRow={(fileId) => {
            // 편집 모달(z-50)이 미리보기(z-60)에 가리지 않도록 미리보기를 닫고 연다.
            setShowPreview(false);
            setEditingFileId(fileId);
          }}
        />
      )}

      {editingFile && (
        <EditRecordModal
          jobId={jobId}
          file={editingFile}
          onClose={() => {
            setEditingFileId(null);
            // 편집으로 값이 바뀌면 경고/차단 상태도 갱신되도록 재조회.
            void qa.refetch();
          }}
        />
      )}
    </section>
  );
}
